import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getAuthClient } from '../firebaseConfig';

function normalizeApp(value = '') {
  const v = String(value || '').toLowerCase();
  if (['link', 'ontime', 'on_time', 'dispatch'].includes(v)) return 'ontime';
  if (['mc', 'morpheus', 'morpheus-count', 'count'].includes(v)) return 'mc';
  if (['neo', 'cashup', 'cash-up', 'operations'].includes(v)) return 'neo';
  return v || 'neo';
}

const LAYERS = {
  neo: {
    label: 'Neo',
    sub: 'Operations Engine',
    dot: '#3b82f6',
    glow: 'rgba(56,189,248,.24)',
    gradient: 'from-cyan-300 via-sky-400 to-blue-500',
    bg: 'radial-gradient(circle at 50% 46%, rgba(34,211,238,.20), transparent 34%), radial-gradient(circle at 88% 12%, rgba(59,130,246,.20), transparent 36%), linear-gradient(135deg, #06121f 0%, #07162a 52%, #050814 100%)',
    name: 'Neo',
  },
  mc: {
    label: 'Morpheus',
    sub: 'Count Intelligence',
    dot: '#8b5cf6',
    glow: 'rgba(139,92,246,.24)',
    gradient: 'from-violet-300 via-purple-400 to-fuchsia-500',
    bg: 'radial-gradient(circle at 50% 46%, rgba(139,92,246,.20), transparent 34%), radial-gradient(circle at 88% 12%, rgba(217,70,239,.16), transparent 36%), linear-gradient(135deg, #110b25 0%, #1a1038 52%, #060412 100%)',
    name: 'Morpheus Count',
  },
  ontime: {
    label: 'Link',
    sub: 'Dispatch Layer',
    dot: '#fb923c',
    glow: 'rgba(34,211,238,.22)',
    gradient: 'from-cyan-300 via-sky-300 to-violet-400',
    bg: 'radial-gradient(circle at 50% 46%, rgba(34,211,238,.20), transparent 34%), radial-gradient(circle at 88% 12%, rgba(124,58,237,.16), transparent 36%), linear-gradient(135deg, #06121f 0%, #071a24 52%, #0b0a22 100%)',
    name: 'Link',
  },
};

export default function NoAccessPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const preferredApp = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return normalizeApp(location.state?.preferredApp || params.get('app') || location.state?.app || 'neo');
  }, [location.search, location.state]);

  const layer = LAYERS[preferredApp] || LAYERS.neo;
  const message =
    location.state?.accessDeniedMessage ||
    `This account does not have access to ${layer.name}. Ask an admin to activate ${layer.name} for this username.`;

  const goBack = async () => {
    try {
      await signOut(getAuthClient());
    } catch (_) {}
    navigate('/login', { replace: true, state: { preferredApp } });
  };

  const contactAdmin = async () => {
    try {
      await signOut(getAuthClient());
    } catch (_) {}
    navigate('/contact', { replace: true });
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white" style={{ background: layer.bg }}>
      <style>{`
        @keyframes matrixosAccessPop {
          0% { transform: translateY(12px) scale(.96); opacity: 0; filter: blur(8px); }
          100% { transform: translateY(0) scale(1); opacity: 1; filter: blur(0); }
        }
        @keyframes matrixosAccessScan {
          0% { transform: translateX(-120%); opacity: .25; }
          35% { opacity: 1; }
          100% { transform: translateX(220%); opacity: .9; }
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:76px_76px]" />
        <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" style={{ backgroundColor: layer.glow }} />
        <div className="absolute left-[28%] top-0 h-full w-px rotate-12 bg-cyan-200/16 shadow-[0_0_90px_rgba(34,211,238,.55)]" />
        <div className="absolute right-[28%] top-0 h-full w-px rotate-12 bg-cyan-200/12 shadow-[0_0_90px_rgba(34,211,238,.45)]" />
      </div>

      <div className="relative z-10 grid min-h-screen place-items-center px-5">
        <div className="flex flex-col items-center text-center animate-[matrixosAccessPop_.45s_ease-out_both]">
          <div className="text-7xl font-semibold italic leading-none tracking-[-0.08em] text-white drop-shadow-[0_0_34px_rgba(255,255,255,.36)]">
            {layer.label}<span style={{ color: layer.dot, textShadow: `0 0 24px ${layer.dot}` }}>•</span>
          </div>
          <div className="mt-4 text-xs font-black uppercase tracking-[0.44em] text-cyan-200">{layer.sub}</div>
          <div className="mt-10 h-1.5 w-72 overflow-hidden rounded-full bg-white/10">
            <div className={`h-full w-1/2 rounded-full bg-gradient-to-r ${layer.gradient} shadow-[0_0_28px_rgba(34,211,238,.55)] animate-[matrixosAccessScan_1.7s_cubic-bezier(.2,.85,.25,1)_forwards]`} />
          </div>
          <div className="mt-5 text-sm font-bold uppercase tracking-[0.28em] text-white/70">Access blocked</div>

          <div className="mt-7 w-[min(92vw,520px)] rounded-[1.6rem] border border-red-300/25 bg-red-500/10 p-5 shadow-[0_0_50px_rgba(248,113,113,.18)] backdrop-blur-xl">
            <div className="text-sm font-black uppercase tracking-[0.28em] text-red-200">No access</div>
            <p className="mt-3 text-base leading-7 text-white/85">{message}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={goBack}
                className="rounded-full border border-white/15 bg-white/8 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-white/14"
              >
                Return
              </button>
              <button
                type="button"
                onClick={contactAdmin}
                className={`rounded-full bg-gradient-to-r ${layer.gradient} px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:scale-[1.02]`}
              >
                Contact admin
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
